/**
 * Turns authored `Environment` settings into the concrete conditions of one turn.
 *
 * Wind and time of day are the only live channels and both are constant for a whole race, so this
 * is trivial today. It stays as a seam: rendering reads one `RolledEnvironment` shape, and a later
 * re-roll (gusts, weather) can vary per turn without touching every call site. Pure and Phaser-free.
 */

import type { Environment } from '../map/environment.ts';

export type RolledWind = {
  /** Compass degrees the wind blows towards. */
  directionDeg: number;
  /** Metres per second (same number the editor and HUD show). */
  speed: number;
};

export type RolledEnvironment = {
  turn: number;
  /** 0 is midnight, 0.5 is noon. */
  timeOfDay: number;
  wind: RolledWind;
};

/**
 * Constant conditions from the authored environment. Time off is noon; wind off is calm. The
 * `turn` is carried so a future per-turn roll can key on it without changing the return shape.
 */
export function rollEnvironment(environment: Environment, turn: number): RolledEnvironment {
  const { wind } = environment;

  return {
    turn,
    timeOfDay: environment.timeEnabled ? environment.timeOfDay : 0.5,
    wind: {
      directionDeg: wind.directionDeg,
      speed: wind.enabled ? wind.speed : 0,
    },
  };
}
